// apps/web/src/features/commercial-proposals/components/CommercialProposalEditor.tsx
//
// #1198 create editor host at `/kp/new`. Renders the full grouped field set
// + live-calc preview inside an `EditorDrawer`; POSTs a new DRAFT and
// navigates to its detail drawer on success.
//
// @aiInvariant `EditorDrawer` reads `dirty` so the shared unsaved-changes
//   guard catches Escape / backdrop / Back. `bypassNext()` runs BEFORE
//   `navigate(...)` on submit so the still-dirty local state does not
//   intercept the same-tx navigation. The PUBLISHED-locked / edit path lives
//   in the detail drawer; this host only creates a fresh draft.
//
// D-4 (#2318): the lead card's CP menu item (composed via ROUTES only — see
// `features/crm/leads/card/ArtifactsBlock.tsx`) opens this route with
// `?leadId=` in the query and `{ clientPrefill }` in router STATE (never the
// URL — client/contact text is PII). `leadId` seeds the create POST OUTSIDE
// `formToContent`/`content` (mirrors the #2311 wire contract);
// `clientPrefill` only seeds the editable `client` field.

import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { EditorDrawer, useEditorGuard, useNarrow } from '../../../ui/index.js';
import type { AppLocale } from '../../../i18n/index.js';
import { useAuthStore } from '../../../stores/auth.js';
import { useToastStore } from '../../../stores/toast.js';
import { useCreateCommercialProposal } from '../queries.js';
import { emptyFormState, formToContent, type CommercialProposalFormState } from '../formModel.js';
import {
  useCommercialProposalForm,
  type UseCommercialProposalFormResult,
} from '../useCommercialProposalForm.js';
import { CommercialProposalEditorBody } from './CommercialProposalEditorBody.js';

/**
 * Narrow the router `state` (`unknown` — react-router types it as `any`, but
 * this codebase bans `any`) down to the optional `clientPrefill` string the
 * lead card may have seeded. Any other shape (missing state, direct URL
 * visit, unrelated caller) yields no prefill.
 */
function readClientPrefill(state: unknown): string {
  if (typeof state !== 'object' || state === null || !('clientPrefill' in state)) return '';
  const value = (state as { clientPrefill: unknown }).clientPrefill;
  return typeof value === 'string' ? value : '';
}

export function CommercialProposalEditor() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const narrow = useNarrow();
  const close = () => navigate('/kp');
  const leadId = searchParams.get('leadId');
  const clientPrefill = readClientPrefill(location.state);
  // D13: seed the manager block from the signed-in user. The auth bootstrap is
  // two-step, so `user` can be null on the first render. Gate the editable form
  // until `user` is known (gate-on-user), then build `emptyFormState()` ONCE
  // with the user's name/phone — `useCommercialProposalForm` captures its
  // `initial` once, so seeding before mount is the only place that cannot wipe
  // an operator edit (no edit can exist before the form mounts).
  const user = useAuthStore((s) => s.user);

  if (!user) {
    return (
      <EditorDrawer dirty={false} busy={false} onConfirmClose={close} width={680} narrow={narrow}>
        <ManagerLoading />
      </EditorDrawer>
    );
  }

  return (
    <CommercialProposalEditorHost
      initialForm={{
        ...emptyFormState(),
        managerName: user.name,
        managerPhone: user.phone,
        client: clientPrefill,
      }}
      leadId={leadId}
      close={close}
      narrow={narrow}
    />
  );
}

function ManagerLoading() {
  const { t } = useTranslation('commercialProposals');
  return (
    <p
      data-testid="commercial-proposals-editor-loading"
      style={{ color: 'var(--fg-3)', fontSize: 13 }}
    >
      {t('drawer.loading')}
    </p>
  );
}

function CommercialProposalEditorHost({
  initialForm,
  leadId,
  close,
  narrow,
}: {
  initialForm: CommercialProposalFormState;
  leadId: string | null;
  close: () => void;
  narrow: boolean;
}) {
  const formApi = useCommercialProposalForm(initialForm);
  const createMutation = useCreateCommercialProposal();
  const submitting = createMutation.isPending;

  return (
    <EditorDrawer
      dirty={formApi.dirty}
      busy={submitting}
      onConfirmClose={close}
      width={680}
      narrow={narrow}
    >
      <CommercialProposalEditorInner
        formApi={formApi}
        submitting={submitting}
        onClose={close}
        createMutation={createMutation}
        leadId={leadId}
      />
    </EditorDrawer>
  );
}

function CommercialProposalEditorInner({
  formApi,
  submitting,
  onClose,
  createMutation,
  leadId,
}: {
  formApi: UseCommercialProposalFormResult;
  submitting: boolean;
  onClose: () => void;
  createMutation: ReturnType<typeof useCreateCommercialProposal>;
  leadId: string | null;
}) {
  const { t, i18n } = useTranslation('commercialProposals');
  const { bypassNext } = useEditorGuard();
  const navigate = useNavigate();
  const showToast = useToastStore((s) => s.show);
  const { form, setField, preview, valid } = formApi;

  const submit = () =>
    createMutation.mutate(
      // D-4 (#2318): `leadId` travels OUTSIDE `content` (mirrors the #2311
      // wire contract) — it never enters the form model or the CP content.
      { content: formToContent(form, null), ...(leadId ? { leadId } : {}) },
      {
        onSuccess: (resp) => {
          bypassNext();
          navigate(`/kp/${encodeURIComponent(resp.proposal.id)}`);
        },
        onError: () => showToast({ tone: 'error', text: t('editor.createError') }),
      },
    );

  return (
    <CommercialProposalEditorBody
      title={t('editor.title')}
      form={form}
      setField={setField}
      preview={preview}
      valid={valid}
      language={i18n.language as AppLocale}
      submitting={submitting}
      submitLabel={submitting ? t('editor.creating') : t('editor.create')}
      onClose={onClose}
      onSubmit={submit}
    />
  );
}
